import { IContentImage } from '@casinocore/type-utils';

import { Dimension, IContentLink, ISchedule, ImageType, ImmersiveLobbyConfig, LobbyGameInfo } from './common.model';
import { GameLobbyModel } from './game-lobby.model';

export const IMMERSIVELOBBY_ONBOARDING_COOKIE_NAME = 'hideImmersiveLobbyOnboarding';
export const IMMERSIVELOBBY_STATS_CTA_TYPE_GAME = 'Game';
export const IMMERSIVELOBBY_STATS_CTA_TYPE_STATIC_GAME = 'StaticGame';
export const IMMERSIVELOBBY_STATS_CTA_TYPE_SECTOR = 'Sector';
export const IMMERSIVELOBBY_ITEM_TYPE_GAME_LOGO = 'GameLogo';
export const IMMERSIVELOBBY_ITEM_TYPE_GAME = 'Game';
export const IMMERSIVELOBBY_ITEM_TYPE_CUSTOM_LINK = 'CustomLink';
export const IMMERSIVELOBBY_CUSTOM_LINK_TYPE_JACKPOT_DRAWER = 'JackpotDrawer';
export const IMMERSIVELOBBY_GSHUB_ONBOARDING_COOKIE_NAME = 'hideGSHubOnboarding';

export interface ImmersiveLobbyIContentParameters {
    CustomLinkDrawerBgClr: string;
    CustomLinkDrawerHeaderClr: string;
    GlowingIconColor: string;
}
export interface IImmersiveLobbyMap {
    width: number;
    height: number;
    backgroundImage: IContentImage;
    layeredImageType: string;
    layeredImage: string;
    lottieImage?: string;
    audioFile?: string;
    sectors: Array<IImmersiveLobbySector>;
    condition: string;
}

export interface IImmersiveLobbySector {
    lmtCategoryId: string;
    categoryId: string;
    categoryName: string;
    top: number;
    left: number;
    width: number;
    height: number;
    imageType: string;
    sectorImage: string;
    sectorIcon: IContentImage;
    lottieImage: string;
    zoomedOutClipPath: string;
    zoomedInClipPath: string;
    zoomForDesktop: number;
    zoomForMobile: number;
    sectorType: string;
    hiddenSectorCTALink: IContentLink;
    displayJackpotTicker: boolean;
    jackpotTickerText?: string;
    jackpotTickerImage?: IContentImage;
    customLinks: Array<IImmersiveLobbySectorItem>;
    customLinksGridColumns: number;
    customLinksPosition: string;
    gameLogos: Array<IImmersiveLobbySectorItem>;
    games: Array<IImmersiveLobbySectorItem>;
    gamesGridColumns: number;
    gamesDisplayType: string;
    gamesDisplayTypeMobile: string;
    gamesDisplayTypeDesktop: string;
    schedules: Array<ISchedule>;
    isDirectGameLaunch: boolean;
    customLinksGridWidth: number;

    // Custom Props
    categoryIndex: number;
    sectorContainerId: string;
    isLoaded: boolean;
    type: ImageType;
    isDisabled: boolean;
    lobbyGames: Array<GameLobbyModel>;
    //live Lounge
    gameItems: Array<ImmersiveLobbySectorItemContent>;
    customLinkItems: Array<ImmersiveLobbySectorItemContent>;
    condition: string;
}
export interface IImmersiveLobbySectorItem extends LobbyGameInfo {
    width: number;
    height: number;
    top: number;
    left: number;
    itemType: string;
    glowingIconType: string;
    gameIconType: string;
    configurationsContent: any;
    constantMessages: any;
    condition: string;
    customLinkType?: string;
    customLinkIcon?: IContentImage;
    iconType: string;
    customLink?: IContentLink;
    customLinkDrawerTitle?: string;
    customLinkDrawerBackgroundImage: IContentImage;
    customLinkDrawerContent?: Array<string>;
    messages: any;
    // Custom Props
    isLoaded: boolean;
    dimension: Dimension;
    isSquareIcon: boolean;
    showTimer: boolean;
    isOffline: boolean;
    schedule: string | undefined;
    timerPosition: string | undefined;
    cmsTimer: any;
}

export class ImmersiveLobbySectorItemContent {
    isGame: boolean;
    rowSpan: number;
    colSpan: number;
    iconType: string;
    game: GameLobbyModel;
    isDisabled: boolean;
    customLink: IImmersiveLobbySectorItem;
}
export interface IImmersiveLobbyJackpotDrawerContent {
    jackpotGroupName: string;
    jackpotImage?: IContentImage;
    jackpotContent: string;
    displayJackpotTicker: boolean;
    howItWorks: string;
    howCanIParticipate: string;
    terms: string;
    condition: string;
    // Custom Props
    path: string;
}

export interface IImmersiveLobbyStatCategory {
    categoryId: string;
    categoryName: string;
    categoryIcon: IContentImage;
    showHighlights: boolean;
}

export interface IImmersiveLobbyStatItem {
    itemId: string;
    itemName: string;
    itemIcon: IContentImage;
    showCTA: boolean;
    ctaText: string;
    ctaType: string;
    sectorId: string;
    staticGameId: string;
    showTimeFrame: boolean;
    timeFrame: string;
    dataType: string;

    // Custom Props
    value: string;
    gameId: string;
    gameName: string;
    betTS: string;
    isStaticGame: boolean;
    isDynamicGame: boolean;
}

export interface IGameTableNameMapping {
    physicalName: string;
    gameTableName: string;
    commercialGameTableName: string;
}

export interface IImmersiveLobbyStats {
    welcomeTitle: string;
    welcomeDescription: string;
    welcomeBackgroundImage: IContentImage;
    statsDrawerTitle: string;
    statsDrawerBackgroundColor: string;
    statsDrawerHeaderColor: string;
    statsDrawerBackgroundImage: IContentImage;
    statsEmptyMessage: string;
    statsLoginMessage: string;
    statsLoginCategoryName: string;
    statsFooterContent: string;
    statsErrorMessage: string;
    statsUpdateErrorMessage: string;
    categories: Array<IImmersiveLobbyStatCategory>;
    stats: Array<IImmersiveLobbyStatItem>;
    gameTableNames: Array<IGameTableNameMapping>;
    condition: string;
}

export class ImmersiveLobbyStat {
    category: IImmersiveLobbyStatCategory;
    stats: Array<IImmersiveLobbyStatItem>;
    impStats: Array<IImmersiveLobbyStatItem>;
    isAvailable: boolean;

    constructor() {
        this.stats = [];
        this.impStats = [];
        this.isAvailable = false;
    }
}

export class ImmersiveLobbyConfiguration extends ImmersiveLobbyConfig {
    enabled: boolean;
    defaultCategoryId: string;
    statCategoriesDataPath: string;
    statItemsDataPath: string;
    statsDataPath: string;
    enableAudioButton: boolean;
    enableGameNameMapping: boolean;
    enableStats: boolean;
    triviaGameType: string;
    noOfHours: number;
    tiltingPhoneMessageImageUrl: string;
    scheduleGames: Array<string>;
    jackpotGroupName: string;
    //enableSupportForLandscape: boolean;
}

export interface IImmersiveLobbyGlobalConfig {
    StatsPreloginBtnText: string;
    noOfDays: string;
    offlineTableContent: string;
    tiltingMessage: string;
}

export interface IImmersiveLobbyGlobalClrConfig {
    mapBgClr: string;
    statsIconBgColor: string;
    statsThemeIcon: string;
    offlineTableTextClr: string;
    glowingIconClr: string;
    jackpotTickeringBgClr: string;
    jackpotTickeringTextClr: string;
}

export interface IDrawerContent {
    content: Array<IImmersiveLobbyJackpotDrawerContent>;
}
